import { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';
import { Camera, X, ZoomIn, Trash2, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PhotoUploader = ({ photos = [], onChange, maxPhotos = 5, disabled = false }) => {
  const fileInputRef = useRef(null);
  const [previewImage, setPreviewImage] = useState(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const remainingSlots = maxPhotos - photos.length;
    const filesToProcess = files.slice(0, remainingSlots);

    filesToProcess.forEach((file) => {
      if (!file.type.startsWith('image/')) return;
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result;
        onChange([...photos, base64]);
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    onChange(newPhotos);
  };

  return (
    <div className="space-y-3">
      {/* Photo Grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photos.map((photo, index) => (
            <div key={photo} className="relative group aspect-square">
              <img
                src={photo}
                alt={`Foto ${index + 1}`}
                className="w-full h-full object-cover rounded-sm border border-slate-700"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setPreviewImage(photo)}
                  className="w-8 h-8 text-white hover:bg-white/20"
                >
                  <ZoomIn className="w-4 h-4" />
                </Button>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removePhoto(index)}
                    className="w-8 h-8 text-rose-400 hover:bg-rose-500/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Button */}
      {!disabled && photos.length < maxPhotos && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="w-full border-dashed border-slate-700 text-slate-400 hover:text-white hover:border-sky-500 hover:bg-sky-500/10"
          >
            <Camera className="w-4 h-4 mr-2" />
            Añadir Fotos ({photos.length}/{maxPhotos})
          </Button>
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl p-2">
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setPreviewImage(null)}
              className="absolute top-2 right-2 z-10 bg-black/50 text-white hover:bg-black/70"
            >
              <X className="w-4 h-4" />
            </Button>
            {previewImage && (
              <img
                src={previewImage}
                alt="Preview"
                className="w-full h-auto max-h-[80vh] object-contain rounded-sm"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const PhotoGallery = ({ photos = [] }) => {
  const [previewImage, setPreviewImage] = useState(null);

  if (!photos || photos.length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-500 uppercase tracking-wider">Fotos adjuntas</p>
      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
        {photos.map((photo, index) => (
          <div 
            key={photo} 
            className="aspect-square cursor-pointer group relative"
            onClick={() => setPreviewImage(photo)}
          >
            <img
              src={photo}
              alt={`Foto ${index + 1}`}
              className="w-full h-full object-cover rounded-sm border border-slate-700"
            />
            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <ZoomIn className="w-4 h-4 text-white" />
            </div>
          </div>
        ))}
      </div>

      {/* Preview Dialog */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl p-2">
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setPreviewImage(null)}
              className="absolute top-2 right-2 z-10 bg-black/50 text-white hover:bg-black/70"
            >
              <X className="w-4 h-4" />
            </Button>
            {previewImage && (
              <img
                src={previewImage}
                alt="Preview"
                className="w-full h-auto max-h-[80vh] object-contain rounded-sm"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export const LazyPhotoGallery = ({ id, type, count = 0 }) => {
  const [photos, setPhotos] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!count) return null;

  const loadPhotos = async () => {
    if (photos) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/${type}/${id}`);
      setPhotos(res.data.photos || []);
    } catch (e) {
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  if (photos) return <PhotoGallery photos={photos} />;

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={loadPhotos}
      disabled={loading}
      className="border-slate-700 text-slate-300 hover:text-white hover:border-sky-500 gap-2"
      data-testid={`load-photos-${id}`}
    >
      <ImageIcon className="w-4 h-4" />
      {loading ? 'Cargando...' : `Ver ${count} foto${count > 1 ? 's' : ''}`}
    </Button>
  );
};
